/**
 * Footnote Inline Tool for Editor.js
 *
 * 본문 중간에 상위첨자 미주 번호 [n]을 삽입합니다.
 * 데이터는 <sup data-footnote="내용">[1]</sup> 형태로 저장됩니다.
 * 미주 내용 편집은 에디터 하단 패널에서 수행합니다.
 * 렌더링 시 Ruby 후처리에서 하단 미주 섹션을 자동 생성합니다.
 */
import './style.scss';
import { isPanelSticky } from './panel';

export default class FootnoteTool {
  static get isInline() {
    return true;
  }

  static get title() {
    return '각주 (⌘⇧F)';
  }

  static get sanitize() {
    return {
      sup: {
        'data-footnote': true,
      },
    };
  }

  constructor({ api }) {
    this.api = api;
    this.button = null;
    this.currentSup = null;
  }

  render() {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add('ce-inline-tool');
    this.button.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text x="1" y="12" font-size="11" font-weight="bold" fill="currentColor">T</text>
      <text x="9" y="8" font-size="7" font-weight="bold" fill="currentColor">1</text>
    </svg>`;
    return this.button;
  }

  renderActions() {
    // 미주 편집은 하단 패널에서 수행 — 인라인 액션 불필요
    const el = document.createElement('div');
    el.hidden = true;
    return el;
  }

  surround(range) {
    if (this.currentSup) {
      // 기존 미주 클릭 → 하단 패널의 해당 항목으로 포커스
      FootnoteTool.scrollToEntry(this.currentSup);
      this.api.inlineToolbar.close();
      return;
    }

    // 새 미주 삽입
    FootnoteTool.insertAt(range);
    this.api.inlineToolbar.close();
  }

  checkState(selection) {
    const sup = FootnoteTool.findSup(selection.anchorNode);
    this.currentSup = sup;
    if (sup) {
      this.button?.classList.add('ce-inline-tool--active');
      return true;
    }
    this.button?.classList.remove('ce-inline-tool--active');
    return false;
  }

  clear() {
    this.currentSup = null;
  }

  // ─── Static helpers (컨트롤러에서도 사용) ────────────────────────

  /**
   * range 위치에 미주를 삽입합니다.
   * @returns {HTMLElement} 삽입된 sup 요소
   */
  static insertAt(range) {
    const editorEl = document.querySelector('.codex-editor');
    if (!editorEl) return null;

    const existingCount = editorEl.querySelectorAll('sup[data-footnote]').length;
    const num = existingCount + 1;

    const sup = document.createElement('sup');
    sup.dataset.footnote = '';
    sup.textContent = `[${num}]`;

    if (!range.collapsed) {
      range.collapse(false);
    }
    range.insertNode(sup);

    // 커서가 sup 뒤에 올 수 있도록 zero-width space 삽입
    const zws = document.createTextNode('\u200B');
    sup.after(zws);

    // 커서를 ZWS 뒤로 이동
    try {
      const sel = window.getSelection();
      const newRange = document.createRange();
      newRange.setStartAfter(zws);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } catch { /* DOM 교체 시 무시 */ }

    // 패널 갱신 + 새 항목 포커스 (DOM 순서 기준 인덱스)
    const allSups = editorEl.querySelectorAll('sup[data-footnote]');
    const newIndex = Array.from(allSups).indexOf(sup);
    document.dispatchEvent(new CustomEvent('footnote:changed', {
      detail: { focusIndex: newIndex >= 0 ? newIndex : allSups.length - 1 }
    }));

    return sup;
  }

  /**
   * 주어진 노드에서 가장 가까운 data-footnote sup 요소를 찾습니다.
   */
  static findSup(node) {
    if (!node) return null;
    let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (el && el.tagName !== 'DIV') {
      if (el.tagName === 'SUP' && el.hasAttribute('data-footnote')) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  /**
   * 하단 패널의 해당 미주 입력란으로 스크롤 & 포커스합니다.
   */
  static scrollToEntry(sup) {
    const editorEl = document.querySelector('.codex-editor');
    if (!editorEl) return;
    const sups = Array.from(editorEl.querySelectorAll('sup[data-footnote]'));
    const index = sups.indexOf(sup);
    if (index < 0) return;

    const panel = document.querySelector('.cdx-footnote-panel');
    if (!panel) return;
    const entries = panel.querySelectorAll('.cdx-footnote-panel__entry');
    if (entries[index]) {
      if (!isPanelSticky()) {
        entries[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      entries[index].querySelector('input')?.focus();
    }
  }
}
